import React from 'react';
// @ts-ignore
window.React = React;
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import { BrowserRouter } from 'react-router-dom';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from './lib/queryClient';
import { AuthProvider } from './context/AuthContext';
import { AuthBootstrapGate } from './components/auth/AuthBootstrapGate';
import { UserPreferencesProvider } from './context/UserPreferencesContext';
import { ServiceProvider } from './context/ServiceContext';
import { PaymentProvider } from './context/PaymentContext';
import { ThemeProvider } from './context/ThemeContext';
import App from './App';
import { testConnection } from './utils/testConnection';
import { Capacitor } from '@capacitor/core';
import { initCapacitor } from './utils/capacitor';
import { prepareSupabaseAuthStorage } from './services/supabase/authStorage';
import { isNativeUi } from './utils/nativeUi';
import { initSentry } from './utils/sentry';
import { initGA } from './utils/analytics';
import { initMixpanelAnalytics } from './analytics/analytics';
import { initPostHog } from './utils/posthog';
import { AnalyticsProvider } from './analytics/AnalyticsProvider';
import { initWebVitals } from './utils/webVitals';
import './index.css';
import './styles/native-app.css';
import './styles/profile-app-style.css';
import './styles/request-app-style.css';
import './styles/search-app-style.css';
import './styles/craftsman-profile-app-style.css';
import './styles/craftsman-jobs-app-style.css';
import './styles/company-profile-app-style.css';
import './styles/company-jobs-app-style.css';
import './styles/company-dashboard-app-style.css';
import './styles/company-earnings-app-style.css';
import './styles/company-employees-app-style.css';
import './styles/auth-app-style.css';
import './styles/customer-dashboard-app-style.css';
import './styles/messages-app-style.css';
import './styles/detail-app-style.css';
import './styles/notifications-reviews-app-style.css';
import './styles/payment-app-style.css';
import './styles/service-history-app-style.css';
import './styles/employee-app-style.css';
import './styles/more-app-style.css';
import './styles/tracking-app-style.css';
import './styles/completion-app-style.css';
import './styles/craftsman-setup-app-style.css';
import './styles/craftsmen-app-style.css';
import './styles/info-app-style.css';
import './styles/analytics-app-style.css';
import './styles/legal-app-style.css';
import './styles/home-app-style.css';
import './styles/app-shell-dark.css';

if (isNativeUi()) {
  document.documentElement.classList.add('native-app');
}

const CHUNK_RELOAD_GUARD_KEY = '__fixnow_chunk_reload_once__';
const CHUNK_RELOAD_MESSAGE = '偵測到版本更新，正在重新整理...';

const getErrorText = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message?: unknown }).message ?? '');
  }
  return '';
};

const isChunkLoadError = (error: unknown): boolean => {
  const text = getErrorText(error).toLowerCase();
  if (!text) return false;
  return (
    text.includes('chunkloaderror') ||
    text.includes('loading chunk') ||
    text.includes('failed to fetch dynamically imported module') ||
    text.includes('importing a module script failed') ||
    text.includes('dynamically imported module')
  );
};

const showReloadNotice = () => {
  if (typeof document === 'undefined') return;
  if (document.getElementById('chunk-reload-notice')) return;

  const notice = document.createElement('div');
  notice.id = 'chunk-reload-notice';
  notice.textContent = CHUNK_RELOAD_MESSAGE;
  notice.style.position = 'fixed';
  notice.style.left = '50%';
  notice.style.bottom = '24px';
  notice.style.transform = 'translateX(-50%)';
  notice.style.zIndex = '2147483647';
  notice.style.padding = '10px 14px';
  notice.style.borderRadius = '10px';
  notice.style.background = 'rgba(17,24,39,0.92)';
  notice.style.color = '#fff';
  notice.style.fontSize = '14px';
  notice.style.lineHeight = '1.3';
  notice.style.boxShadow = '0 10px 25px rgba(0,0,0,0.22)';
  notice.style.maxWidth = 'calc(100vw - 24px)';
  notice.style.textAlign = 'center';
  document.body.appendChild(notice);
};

const setupChunkLoadRecovery = () => {
  if (typeof window === 'undefined') return;

  const triggerReloadOnce = (reason: string, event?: Event) => {
    if (event && 'preventDefault' in event) {
      event.preventDefault();
    }

    let alreadyReloaded = false;
    try {
      alreadyReloaded = sessionStorage.getItem(CHUNK_RELOAD_GUARD_KEY) === '1';
    } catch {
      alreadyReloaded = false;
    }

    if (alreadyReloaded) {
      console.error('[chunk-recovery] already retried once; skip reload', reason);
      return;
    }

    try {
      sessionStorage.setItem(CHUNK_RELOAD_GUARD_KEY, '1');
    } catch {
      // Ignore quota/security errors and continue with reload
    }

    console.warn('[chunk-recovery] detected chunk preload/import failure, reloading...', reason);
    showReloadNotice();
    window.setTimeout(() => window.location.reload(), 600);
  };

  window.addEventListener(
    'vite:preloadError',
    (event) => {
      triggerReloadOnce('vite:preloadError', event);
    },
    { capture: true }
  );

  window.addEventListener(
    'error',
    (event) => {
      const errEvent = event as ErrorEvent;
      const errSource = errEvent.error ?? errEvent.message;
      if (isChunkLoadError(errSource)) {
        triggerReloadOnce('window.error', event);
      }
    },
    { capture: true }
  );

  window.addEventListener(
    'unhandledrejection',
    (event) => {
      const rejectionEvent = event as PromiseRejectionEvent;
      if (isChunkLoadError(rejectionEvent.reason)) {
        triggerReloadOnce('unhandledrejection', event);
      }
    },
    { capture: true }
  );
};

const clearChunkReloadGuardAfterBoot = () => {
  if (typeof window === 'undefined') return;
  window.setTimeout(() => {
    try {
      sessionStorage.removeItem(CHUNK_RELOAD_GUARD_KEY);
    } catch {
      // Ignore storage errors
    }
  }, 10_000);
};

setupChunkLoadRecovery();

// 初始化 Sentry 錯誤監控
initSentry();

// 初始化 Google Analytics
initGA();
initMixpanelAnalytics();
initPostHog();
initWebVitals();

// 檢查必要的環境變數
const requiredEnvVars = [
  'VITE_SUPABASE_URL',
  'VITE_SUPABASE_ANON_KEY',
  'VITE_MAPBOX_TOKEN'
];

const missingVars = requiredEnvVars.filter(varName => !import.meta.env[varName]);

if (missingVars.length > 0) {
  console.warn('⚠️ Missing environment variables:', missingVars);
  console.warn('Please check your .env file');
} else {
  console.log('✅ All environment variables found');
  // 測試數據庫連接
  testConnection();
}

// 在 Capacitor 環境中註銷 Service Worker，避免快取導致的更新問題
if (navigator.serviceWorker) {
  navigator.serviceWorker.getRegistrations().then((registrations) => {
    for (const registration of registrations) {
      registration.unregister();
    }
  });
}

async function bootApp(): Promise<void> {
  if (Capacitor.isNativePlatform()) {
    await initCapacitor();
    await prepareSupabaseAuthStorage();
  } else {
    void initCapacitor();
  }

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <HelmetProvider>
          <BrowserRouter>
            <ThemeProvider>
              <AuthProvider>
                <AuthBootstrapGate>
                  <AnalyticsProvider>
                    <UserPreferencesProvider>
                      <ServiceProvider>
                        <PaymentProvider>
                          <App />
                        </PaymentProvider>
                      </ServiceProvider>
                    </UserPreferencesProvider>
                  </AnalyticsProvider>
                </AuthBootstrapGate>
              </AuthProvider>
            </ThemeProvider>
          </BrowserRouter>
        </HelmetProvider>
      </QueryClientProvider>
    </React.StrictMode>
  );

  clearChunkReloadGuardAfterBoot();
}

void bootApp();
